import { useState, useEffect } from 'react';
import axios from 'axios';
import api from '../api';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { isChairmanReadOnly } from '../utils/access';

const API = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/mifl';

const DOC_COLORS  = { MDN: '#e74c3c', PCN: '#27ae60', MCN: '#2980b9', CUN: '#8e44ad' };
const DOC_LABELS  = { MDN: 'Member Debit Note', PCN: 'Payment Confirmation Note', MCN: 'Member Credit Note', CUN: 'Credit Utilisation Notice' };
const DOC_GUIDE   = {
  MDN: 'Post when member misses a payment deadline. Debit only. Member moves to In Arrears.',
  PCN: 'Post when member pays against an MDN debit. Credit only. Reduces or clears arrears.',
  MCN: 'Post when member overpays. Credit only. Member moves to In Credit.',
  CUN: 'Post when a credit balance is applied to offset a new obligation. Enter both debit and credit.'
};
const STATUS_COLORS = { good_standing: '#1e8449', in_arrears: '#e74c3c', in_credit: '#2980b9' };
const STATUS_LABELS = { good_standing: 'Good Standing', in_arrears: 'In Arrears', in_credit: 'In Credit' };

// ── PDF HELPERS ───────────────────────────────────────────
const addLetterhead = (doc, docTitle, refNumber, entryDate) => {
  doc.setFillColor(44, 62, 80); doc.rect(0, 0, 210, 28, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(18); doc.setFont('helvetica', 'bold');
  doc.text('KIGEZI ASSOCIATION', 105, 12, { align: 'center' });
  doc.setFontSize(11); doc.setFont('helvetica', 'normal');
  doc.text(docTitle, 105, 21, { align: 'center' });
  doc.setTextColor(0, 0, 0); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(`Ref: ${refNumber}`, 15, 36); doc.text(`Date: ${entryDate}`, 195, 36, { align: 'right' });
  doc.setDrawColor(44, 62, 80); doc.setLineWidth(0.5); doc.line(15, 39, 195, 39);
  return 45;
};
const addMemberBlock = (doc, y, entry) => {
  doc.setFillColor(240, 240, 240); doc.rect(15, y, 180, 22, 'F');
  doc.setFontSize(9); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('MEMBER DETAILS', 18, y + 6);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  doc.text(`Name: ${entry.member_name}`, 18, y + 13); doc.text(`Member No: ${entry.member_number}`, 18, y + 19);
  doc.text(`Phone: ${entry.phone || 'N/A'}`, 110, y + 13); doc.text(`Email: ${entry.email || 'N/A'}`, 110, y + 19);
  return y + 28;
};
const addFooter = (doc) => {
  const ph = doc.internal.pageSize.height;
  doc.setFillColor(44, 62, 80); doc.rect(0, ph - 15, 210, 15, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(8); doc.setFont('helvetica', 'normal');
  doc.text('Kigezi Association — Official Document — Confidential', 105, ph - 6, { align: 'center' });
};
const addSignatureBlock = (doc, y, labels, entry) => {
  const colWidth = 170 / labels.length;
  if (entry.signature_snapshot) {
    try {
      const fmt = entry.signature_snapshot.includes('data:image/png') ? 'PNG' : 'JPEG';
      doc.setFillColor(255, 255, 255); doc.rect(15, y - 12, 42, 11, 'F');
      doc.addImage(entry.signature_snapshot, fmt, 15, y - 12, 40, 10);
    } catch (e) {}
  }
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  labels.forEach((label, i) => {
    const x = 15 + (i * colWidth);
    doc.setDrawColor(100, 100, 100); doc.line(x, y, x + colWidth - 5, y);
    doc.text(label, x, y + 5);
    if (i === 0 && entry.authorised_by) {
      doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
      doc.text(entry.officer_name || 'Finance Committee', x, y + 11);
      doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
      doc.text('For Kigezi Association', x, y + 16);
      doc.setTextColor(0, 0, 0); doc.setFontSize(9);
    } else if (i === 1) {
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
      doc.text(entry.officer_title || 'Finance Committee', x, y + 11);
      doc.text(`Date: ${entry.entry_date?.slice(0, 10) || ''}`, x, y + 16);
      doc.setFontSize(9);
    } else { doc.text('Date: ___________', x, y + 11); }
  });
  return y + 22;
};
const addClause = (doc, y, text, indent = 15) => {
  doc.setFontSize(9); doc.setFont('helvetica', 'normal'); doc.setTextColor(0, 0, 0);
  const lines = doc.splitTextToSize(text, 195 - indent);
  doc.text(lines, indent, y);
  return y + (lines.length * 5) + 3;
};

// ── DOCUMENT GENERATORS ───────────────────────────────────
const generateEntryDocument = async (entry, headers) => {
  const type = entry.document_type;
  const entryDate = entry.entry_date?.slice(0, 10) || new Date().toLocaleDateString('en-GB');
  if (type === 'MDN') generateMDN(entry, entryDate);
  else if (type === 'PCN') {
    // Fetch allocations for this PCN
    let allocations = [];
    try {
      const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000') + '/api/mifl';
      const res = await axios.get(`${API_BASE}/${entry.id}/allocations`, { headers });
      allocations = res.data;
    } catch (e) { allocations = []; }
    generatePCN(entry, entryDate, allocations);
  }
  else if (type === 'MCN') generateMCN(entry, entryDate);
  else if (type === 'CUN') generateCUN(entry, entryDate);
};

const generateMDN = (entry, entryDate) => {
  const doc = new jsPDF();
  let y = addLetterhead(doc, 'MEMBER DEBIT NOTE (MDN)', entry.document_number, entryDate);
  y = addMemberBlock(doc, y, entry);
  doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(231, 76, 60);
  doc.text('NOTICE OF FINANCIAL OBLIGATION', 105, y + 6, { align: 'center' });
  doc.setTextColor(0, 0, 0); y += 14;
  autoTable(doc, {
    startY: y, head: [['Field', 'Details']],
    body: [
      ['Nature of Obligation', entry.description], ['Due Date / Entry Date', entryDate],
      ['Debit Amount (UGX)', Number(entry.debit || 0).toLocaleString()],
      ['Balance After This Entry (UGX)', Number(entry.balance || 0).toLocaleString()],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Your MIFL account has been debited. This obligation must be cleared at the earliest opportunity.', 15, y, { maxWidth: 180 }); y += 10;
  doc.text('Continued non-payment will result in escalation per the Association Constitution.', 15, y, { maxWidth: 180 }); y += 20;
  y = addSignatureBlock(doc, y, ['Prepared By (Finance Committee)', 'Position'], entry);
  addFooter(doc);
  doc.save(`MDN-${entry.member_number}-${entry.document_number}.pdf`);
};

const generatePCN = (entry, entryDate, allocations = []) => {
  const doc = new jsPDF();
  let y = addLetterhead(doc, 'PAYMENT CONFIRMATION NOTE (PCN)', entry.document_number, entryDate);
  y = addMemberBlock(doc, y, entry);
  autoTable(doc, {
    startY: y, head: [['Field', 'Details']],
    body: [
      ['Description / Reference', entry.description], ['Date of Payment', entryDate],
      ['Total Amount Received (UGX)', Number(entry.credit || 0).toLocaleString()],
      ['Balance After This Entry (UGX)', Number(entry.balance || 0).toLocaleString()],
      ['Account Status', parseFloat(entry.balance) <= 0 ? 'CLEARED / IN CREDIT' : 'PARTIALLY SETTLED — Balance Remains'],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 8;

  // MDN Allocation Table
  if (allocations && allocations.length > 0) {
    doc.setFontSize(10); doc.setFont('helvetica', 'bold'); doc.setTextColor(26, 82, 118);
    doc.text('MDN ALLOCATION DETAIL — Obligations Cleared by This Payment', 15, y); y += 4;
    autoTable(doc, {
      startY: y,
      head: [['MDN Reference', 'Description', 'Original (UGX)', 'Applied (UGX)', 'Remaining (UGX)', 'Status']],
      body: allocations.map(a => [
        a.mdn_number || a.document_number || '—',
        (a.mdn_description || a.description || '').slice(0, 50),
        Number(a.mdn_original || a.debit || 0).toLocaleString(),
        Number(a.amount_applied || 0).toLocaleString(),
        Number(a.mdn_remaining || 0).toLocaleString(),
        (a.mdn_status || 'settled').toUpperCase(),
      ]),
      styles: { fontSize: 9 }, headStyles: { fillColor: [26, 82, 118] },
      columnStyles: {
        2: { halign: 'right' }, 3: { halign: 'right', fontStyle: 'bold' },
        4: { halign: 'right' }, 5: { halign: 'center' }
      },
      margin: { left: 15, right: 15 },
    });
    y = doc.lastAutoTable.finalY + 6;
    // Totals row
    const totalApplied = allocations.reduce((s, a) => s + parseFloat(a.amount_applied || 0), 0);
    doc.setFillColor(26, 82, 118); doc.rect(15, y, 180, 10, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(`Total Applied to MDNs: UGX ${totalApplied.toLocaleString()}`, 105, y + 7, { align: 'center' });
    doc.setTextColor(0, 0, 0); y += 16;
  }

  // MCN reference if overpayment
  if (entry.auto_mcn_number) {
    doc.setFillColor(39, 174, 96); doc.rect(15, y, 180, 12, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    doc.text(`Excess of UGX ${Number(entry.auto_mcn_amount || 0).toLocaleString()} forwarded to ${entry.auto_mcn_number} as credit balance`, 105, y + 8, { align: 'center', maxWidth: 170 });
    doc.setTextColor(0, 0, 0); y += 18;
  }

  const settled = parseFloat(entry.balance) <= 0;
  doc.setFillColor(settled ? 39 : 243, settled ? 174 : 156, settled ? 96 : 18);
  doc.rect(15, y, 180, 14, 'F');
  doc.setTextColor(255, 255, 255); doc.setFontSize(10); doc.setFont('helvetica', 'bold');
  doc.text(settled ? 'ACCOUNT CLEARED — THANK YOU' : `PARTIAL PAYMENT — BALANCE REMAINS: UGX ${Number(entry.balance).toLocaleString()}`, 105, y + 9, { align: 'center', maxWidth: 170 });
  doc.setTextColor(0, 0, 0); y += 22;
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('This document confirms receipt of the payment and the resulting obligation status in the MIFL.', 15, y, { maxWidth: 180 }); y += 18;
  y = addSignatureBlock(doc, y, ['Issued By (Finance Committee)', 'Position'], entry);
  addFooter(doc);
  doc.save(`PCN-${entry.member_number}-${entry.document_number}.pdf`);
};

const generateMCN = (entry, entryDate) => {
  const doc = new jsPDF();
  let y = addLetterhead(doc, 'MEMBER CREDIT NOTE (MCN)', entry.document_number, entryDate);
  y = addMemberBlock(doc, y, entry);
  autoTable(doc, {
    startY: y, head: [['Field', 'Details']],
    body: [
      ['Description', entry.description], ['Date of Credit', entryDate],
      ['Amount Credited (UGX)', Number(entry.credit || 0).toLocaleString()],
      ['Credit Balance After (UGX)', Number(Math.abs(entry.balance || 0)).toLocaleString()],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  doc.setFontSize(9); doc.setFont('helvetica', 'italic');
  doc.text('The member has paid in excess. Credit will be applied to future obligations via a Credit Utilisation Notice (CUN).', 15, y, { maxWidth: 180 }); y += 14;
  doc.setFont('helvetica', 'normal');
  doc.text('No refunds will be issued. The credit is held on the MIFL until fully utilised.', 15, y, { maxWidth: 180 }); y += 18;
  y = addSignatureBlock(doc, y, ['Issued By (Finance Committee)', 'Acknowledged By (Member)'], entry);
  addFooter(doc);
  doc.save(`MCN-${entry.member_number}-${entry.document_number}.pdf`);
};

const generateCUN = (entry, entryDate) => {
  const doc = new jsPDF();
  let y = addLetterhead(doc, 'CREDIT UTILISATION NOTIFICATION (CUN)', entry.document_number, entryDate);
  y = addMemberBlock(doc, y, entry);
  y = addClause(doc, y, 'This notification confirms that a credit balance has been applied to offset a financial obligation:');
  autoTable(doc, {
    startY: y, head: [['Field', 'Details']],
    body: [
      ['Description / Reference', entry.description], ['Date of Utilisation', entryDate],
      ['Amount Applied — Debit (UGX)', Number(entry.debit || 0).toLocaleString()],
      ['Amount Applied — Credit (UGX)', Number(entry.credit || 0).toLocaleString()],
      ['Resulting MIFL Balance (UGX)', Number(entry.balance || 0).toLocaleString()],
      ['Account Status', parseFloat(entry.balance) > 0 ? 'IN ARREARS' : parseFloat(entry.balance) < 0 ? 'IN CREDIT — Carry Forward' : 'FULLY SETTLED'],
    ],
    styles: { fontSize: 10 }, headStyles: { fillColor: [44, 62, 80] },
    columnStyles: { 0: { fontStyle: 'bold', cellWidth: 80 } }, margin: { left: 15, right: 15 },
  });
  y = doc.lastAutoTable.finalY + 10;
  y = addClause(doc, y, 'Please contact the Finance Committee for any clarification regarding adjustments to your MIFL.');
  y += 10;
  y = addSignatureBlock(doc, y, ['Authorised By (Finance Committee)', 'Position'], entry);
  addFooter(doc);
  doc.save(`CUN-${entry.member_number}-${entry.document_number}.pdf`);
};

// ── STATEMENT PDF ─────────────────────────────────────────
const generateStatementPDF = ({ memberName, memberNumber, entries, filterFrom, filterTo, netDebits, netCredits, netBalance, status }) => {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString('en-UG', { day: '2-digit', month: 'long', year: 'numeric' });

  doc.setFillColor(44, 62, 80); doc.rect(0, 0, pageW, 22, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13); doc.setTextColor(255, 255, 255);
  doc.text('KIGEZI ASSOCIATION', pageW / 2, 10, { align: 'center' });
  doc.setFontSize(9); doc.setFont('helvetica', 'normal');
  doc.text('Member Individual Financial Ledger — Statement of Account', pageW / 2, 17, { align: 'center' });

  doc.setTextColor(0, 0, 0); doc.setFillColor(248, 249, 250); doc.rect(10, 26, pageW - 20, 20, 'F');
  doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
  doc.text(memberName || 'All Members', 15, 33);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  if (memberNumber) doc.text(`Member No: ${memberNumber}`, 15, 39);
  doc.text(`Generated: ${today}`, pageW - 15, 33, { align: 'right' });
  const period = filterFrom && filterTo ? `${filterFrom} to ${filterTo}` : filterFrom ? `From ${filterFrom}` : filterTo ? `To ${filterTo}` : 'All dates';
  doc.text(`Period: ${period}`, pageW - 15, 39, { align: 'right' });

  if (status) {
    const statusText = STATUS_LABELS[status] || status;
    const rgb = (STATUS_COLORS[status] || '#95a5a6').replace('#', '');
    const r = parseInt(rgb.substr(0,2),16), g = parseInt(rgb.substr(2,2),16), b = parseInt(rgb.substr(4,2),16);
    doc.setFillColor(r, g, b); doc.roundedRect(pageW - 60, 27, 45, 8, 2, 2, 'F');
    doc.setTextColor(255, 255, 255); doc.setFont('helvetica', 'bold'); doc.setFontSize(8);
    doc.text(statusText.toUpperCase(), pageW - 37.5, 32.5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
  }

  const sumY = 50;
  const boxes = [
    { label: 'Outstanding Obligations (Net)',  value: `UGX ${Number(netDebits).toLocaleString()}`,           color: [231, 76, 60] },
    { label: 'Unutilised Credit (Net)',         value: `UGX ${Number(netCredits).toLocaleString()}`,          color: [39, 174, 96] },
    { label: 'Net Balance',                     value: `UGX ${Number(Math.abs(netBalance)).toLocaleString()}`, color: netBalance > 0 ? [231, 76, 60] : netBalance < 0 ? [41, 128, 185] : [44, 62, 80] },
    { label: 'Transactions',                    value: String(entries.length),                                 color: [44, 62, 80] },
  ];
  const boxW = (pageW - 20) / 4 - 3;
  boxes.forEach((b, i) => {
    const x = 10 + i * (boxW + 4);
    doc.setFillColor(...b.color); doc.rect(x, sumY, boxW, 14, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
    doc.text(b.label, x + boxW / 2, sumY + 5, { align: 'center' });
    doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
    doc.text(b.value, x + boxW / 2, sumY + 11, { align: 'center' });
  });
  doc.setTextColor(0, 0, 0);

  const tableData = entries.map(e => [
    e.entry_date?.slice(0, 10) || '—', e.document_type, DOC_LABELS[e.document_type] || e.document_type,
    e.document_number || '—', e.description || '—',
    parseFloat(e.debit) > 0 ? Number(e.debit).toLocaleString() : '—',
    parseFloat(e.credit) > 0 ? Number(e.credit).toLocaleString() : '—',
    Number(e.balance || 0).toLocaleString()
  ]);

  autoTable(doc, {
    startY: sumY + 18,
    head: [['Date', 'Type', 'Document', 'Reference No.', 'Description', 'Debit (UGX)', 'Credit (UGX)', 'Balance (UGX)']],
    body: tableData.length > 0 ? tableData : [['', '', '', 'No entries in this period', '', '', '', '']],
    styles: { fontSize: 8, cellPadding: 2.5 }, headStyles: { fillColor: [44, 62, 80], textColor: 255, fontStyle: 'bold', fontSize: 8 },
    columnStyles: { 0: { cellWidth: 22 }, 1: { cellWidth: 14, halign: 'center' }, 2: { cellWidth: 36 }, 3: { cellWidth: 30 }, 4: { cellWidth: 'auto' }, 5: { cellWidth: 28, halign: 'right' }, 6: { cellWidth: 28, halign: 'right' }, 7: { cellWidth: 28, halign: 'right', fontStyle: 'bold' } },
    alternateRowStyles: { fillColor: [248, 249, 250] },
    didParseCell: (data) => {
      if (data.section === 'body') {
        if (data.column.index === 1) {
          const t = data.cell.raw;
          if (t === 'MDN') data.cell.styles.textColor = [231, 76, 60];
          else if (t === 'PCN') data.cell.styles.textColor = [39, 174, 96];
          else if (t === 'MCN') data.cell.styles.textColor = [41, 128, 185];
          else if (t === 'CUN') data.cell.styles.textColor = [142, 68, 173];
          data.cell.styles.fontStyle = 'bold';
        }
        if (data.column.index === 7) {
          const val = parseFloat(data.cell.raw?.replace(/,/g, '') || 0);
          if (val > 0) data.cell.styles.textColor = [231, 76, 60];
          else if (val < 0) data.cell.styles.textColor = [41, 128, 185];
          else data.cell.styles.textColor = [39, 174, 96];
        }
      }
    },
    margin: { left: 10, right: 10 }
  });

  const finalY = doc.lastAutoTable.finalY + 6;
  doc.setFillColor(234, 244, 251); doc.rect(10, finalY, pageW - 20, 10, 'F');
  doc.setFontSize(7.5); doc.setFont('helvetica', 'bold'); doc.setTextColor(44, 62, 80);
  doc.text('Article 18 — Kigezi Association Constitution:', 15, finalY + 4);
  doc.setFont('helvetica', 'normal'); doc.setTextColor(80, 80, 80);
  doc.text('This MIFL statement constitutes the definitive and binding record of all financial obligations. The balance is prima facie evidence in any legal proceeding.', 15, finalY + 8.5, { maxWidth: pageW - 30 });

  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i); doc.setFontSize(8); doc.setTextColor(150);
    doc.text(`Page ${i} of ${pageCount}`, pageW / 2, doc.internal.pageSize.getHeight() - 5, { align: 'center' });
  }
  const filename = memberName ? `MIFL_${memberName.replace(/\s+/g, '_')}_${new Date().toISOString().slice(0,10)}.pdf` : `MIFL_Full_Statement_${new Date().toISOString().slice(0,10)}.pdf`;
  doc.save(filename);
};

// ── COMPONENT ─────────────────────────────────────────────
function Mifl() {
  const [entries, setEntries]         = useState([]);
  const [members, setMembers]         = useState([]);
  const [summary, setSummary]         = useState([]);
  const [view, setView]               = useState('ledger');
  const [showForm, setShowForm]       = useState(false);
  const [editingId, setEditingId]     = useState(null);
  const [editData, setEditData]       = useState({});
  const [msg, setMsg]                 = useState('');
  const [msgType, setMsgType]         = useState('success');
  const [downloading, setDownloading] = useState(false);

  // MDN allocation checklist for PCN
  const [outstandingMdns, setOutstandingMdns] = useState([]);
  const [selectedMdns, setSelectedMdns]       = useState({});
  const [loadingMdns, setLoadingMdns]         = useState(false);

  const fetchOutstandingMdns = async (memberId) => {
    if (!memberId) { setOutstandingMdns([]); setSelectedMdns({}); return; }
    setLoadingMdns(true);
    try {
      const res = await axios.get(`${API}/member/${memberId}/outstanding-mdns`, { headers });
      setOutstandingMdns(res.data);
      setSelectedMdns({});
    } catch { setOutstandingMdns([]); }
    setLoadingMdns(false);
  };

  const toggleMdn = (mdn) => {
    setSelectedMdns(prev => {
      const next = { ...prev };
      if (next[mdn.id]) { delete next[mdn.id]; }
      else { next[mdn.id] = parseFloat(mdn.amount_outstanding); }
      return next;
    });
  };

  const [filterMember,  setFilterMember]  = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [filterFrom,    setFilterFrom]    = useState('');
  const [filterTo,      setFilterTo]      = useState('');

  const [form, setForm] = useState({
    member_id: '', entry_date: new Date().toISOString().split('T')[0],
    document_type: 'MDN', document_number: '', description: '', debit: '', credit: '',
    mode_of_payment: '', receipt_ref: '',
    apply_to_type: '', apply_fine_id: '', apply_rosca_contribution_id: '',
    apply_savings_cycle_id: '', apply_savings_month: '', apply_subscription_cycle_id: '', apply_loan_id: ''
  });

  // CUN "Apply To" picker — the member's available credit plus whichever
  // obligations currently have an outstanding balance a credit could be
  // applied against (fetched fresh whenever the member or document type
  // changes to CUN).
  const [creditTargets, setCreditTargets] = useState(null);
  const [loadingCreditTargets, setLoadingCreditTargets] = useState(false);
  const readOnly = isChairmanReadOnly();

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Computed allocation values — must be after form and selectedMdns declarations
  const totalAllocated      = Object.values(selectedMdns).reduce((s, v) => s + v, 0);
  const pcnAmount           = parseFloat(form.credit || 0);
  const allocationRemaining = pcnAmount - totalAllocated;

  useEffect(() => {
    fetchEntries(); fetchSummary();
    api.get('/api/members').then(r => setMembers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (entries.length >= 0 && !form.document_number) {
      generateRefNumber('MDN').then(ref => setForm(prev => ({ ...prev, document_number: ref })));
    }
  }, [entries]);

  const fetchEntries = (overrides = {}) => {
    const params = {};
    const m  = overrides.member  !== undefined ? overrides.member  : filterMember;
    const dt = overrides.docType !== undefined ? overrides.docType : filterDocType;
    const fr = overrides.from    !== undefined ? overrides.from    : filterFrom;
    const to = overrides.to      !== undefined ? overrides.to      : filterTo;
    if (m)  params.member_id     = m;
    if (dt) params.document_type = dt;
    if (fr) params.from_date     = fr;
    if (to) params.to_date       = to;
    axios.get(API, { headers, params }).then(r => setEntries(r.data)).catch(() => {});
  };

  const fetchSummary = () => {
    axios.get(`${API}/summary`, { headers }).then(r => setSummary(r.data)).catch(() => {});
  };

  const showMessage = (text, type = 'success') => {
    setMsg(text); setMsgType(type); setTimeout(() => setMsg(''), 4000);
  };

  const generateRefNumber = async (type) => {
    const year = new Date().getFullYear();
    try {
      const res = await axios.get(API, { headers, params: { document_type: type } });
      const thisYear = res.data.filter(e => e.document_number?.startsWith(`${type}-${year}-`));
      const maxSeq = thisYear.reduce((max, e) => {
        const seq = parseInt(e.document_number?.split('-')[2] || '0');
        return seq > max ? seq : max;
      }, 0);
      return `${type}-${year}-${String(maxSeq + 1).padStart(4, '0')}`;
    } catch { return `${type}-${year}-0001`; }
  };

  const fetchCreditTargets = async (memberId) => {
    if (!memberId) { setCreditTargets(null); return; }
    setLoadingCreditTargets(true);
    try {
      const res = await axios.get(`${API}/credit-targets/${memberId}`, { headers });
      setCreditTargets(res.data);
    } catch { setCreditTargets(null); }
    setLoadingCreditTargets(false);
  };

  const handleTypeChange = async (type) => {
    const ref = await generateRefNumber(type);
    setForm(prev => ({ ...prev, document_type: type, document_number: ref, debit: '', credit: '', mode_of_payment: '', receipt_ref: '', source_type: '',
      apply_to_type: '', apply_fine_id: '', apply_rosca_contribution_id: '', apply_savings_cycle_id: '', apply_savings_month: '', apply_subscription_cycle_id: '', apply_loan_id: '' }));
    if (type === 'PCN' && form.member_id) fetchOutstandingMdns(form.member_id);
    if (type !== 'PCN') { setOutstandingMdns([]); setSelectedMdns({}); }
    if (type === 'CUN' && form.member_id) fetchCreditTargets(form.member_id); else setCreditTargets(null);
  };

  const handleMemberChange = (memberId) => {
    setForm(prev => ({ ...prev, member_id: memberId,
      apply_to_type: '', apply_fine_id: '', apply_rosca_contribution_id: '', apply_savings_cycle_id: '', apply_savings_month: '', apply_subscription_cycle_id: '', apply_loan_id: '' }));
    if (form.document_type === 'PCN') fetchOutstandingMdns(memberId);
    if (form.document_type === 'CUN') fetchCreditTargets(memberId);
  };

  const handleSubmit = async () => {
    if (!form.member_id || !form.entry_date || !form.document_number || !form.description)
      return showMessage('Fill in all required fields', 'error');
    if (!form.debit && !form.credit)
      return showMessage('Enter either a debit or credit amount', 'error');
    if ((form.document_type === 'PCN' || form.document_type === 'MCN') && !form.mode_of_payment)
      return showMessage('Select the account where payment was received', 'error');
    if (form.document_type === 'CUN') {
      if (!form.apply_to_type) return showMessage('Select what this credit is being applied to', 'error');
      if (form.apply_to_type === 'fine' && !form.apply_fine_id) return showMessage('Select which fine this credit applies to', 'error');
      if (form.apply_to_type === 'rosca' && !form.apply_rosca_contribution_id) return showMessage('No ROSCA contribution currently due for this member', 'error');
      if (form.apply_to_type === 'savings' && !form.apply_savings_cycle_id) return showMessage('No savings instalment currently due for this member', 'error');
      if (form.apply_to_type === 'subscription' && !form.apply_subscription_cycle_id) return showMessage('No subscription balance currently due for this member', 'error');
      if (form.apply_to_type === 'loan' && !form.apply_loan_id) return showMessage('Select which loan this credit applies to', 'error');
      if (form.apply_to_type === 'rosca_upcoming' && !form.apply_rosca_upcoming_contribution_id) return showMessage('No upcoming ROSCA contribution to pay early for this member', 'error');
      if (form.apply_to_type === 'savings_upcoming' && !form.apply_savings_upcoming_cycle_id) return showMessage('No upcoming savings instalment to pay early for this member', 'error');
      if (form.apply_to_type === 'withdrawal' && !form.mode_of_payment) return showMessage('Select which account the withdrawal is paid from', 'error');
      if (creditTargets && parseFloat(form.debit || 0) > creditTargets.available_credit)
        return showMessage(`Amount exceeds available credit (UGX ${creditTargets.available_credit.toLocaleString()})`, 'error');
    }
    try {
      const payload = { ...form };
      if (form.document_type === 'PCN' && Object.keys(selectedMdns).length > 0) {
        payload.allocations = Object.entries(selectedMdns).map(([mdn_id, amount_applied]) => ({
          mdn_id: parseInt(mdn_id), amount_applied
        }));
      }
      const res = await axios.post(API, payload, { headers });
      if (res.data.applied_to) {
        showMessage(`Entry posted — applied to ${res.data.applied_to.label}`, 'success');
      } else if (res.data.auto_mcn) {
        showMessage(`Entry posted. ${res.data.auto_mcn.message}`, 'success');
      } else {
        showMessage('Entry posted successfully');
      }
      const nextRef = await generateRefNumber(form.document_type);
      setForm({ member_id: '', entry_date: new Date().toISOString().split('T')[0], document_type: form.document_type, document_number: nextRef, description: '', debit: '', credit: '', mode_of_payment: '', receipt_ref: '',
        apply_to_type: '', apply_fine_id: '', apply_rosca_contribution_id: '', apply_savings_cycle_id: '', apply_savings_month: '', apply_subscription_cycle_id: '', apply_loan_id: '',
        apply_rosca_upcoming_contribution_id: '', apply_savings_upcoming_cycle_id: '', apply_savings_upcoming_month: '' });
      setCreditTargets(null);
      setShowForm(false);
      fetchEntries(); fetchSummary();
    } catch (err) { showMessage('Error: ' + (err.response?.data?.error || err.message), 'error'); }
  };

  const saveEdit = async (id) => {
    try {
      await axios.patch(`${API}/${id}`, editData, { headers });
      showMessage('Entry updated — balances recalculated');
      setEditingId(null); fetchEntries(); fetchSummary();
    } catch (err) { showMessage('Error: ' + (err.response?.data?.error || err.message), 'error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this entry? All subsequent balances will be recalculated.')) return;
    try {
      await axios.delete(`${API}/${id}`, { headers });
      showMessage('Entry deleted and balances recalculated');
      fetchEntries(); fetchSummary();
    } catch (err) { showMessage('Error: ' + (err.response?.data?.error || err.message), 'error'); }
  };

  const drillToMember = (memberId) => {
    setFilterMember(String(memberId)); setView('ledger');
    fetchEntries({ member: String(memberId) });
  };

  const applyFilters  = () => fetchEntries();
  const clearFilters  = () => {
    setFilterMember(''); setFilterDocType(''); setFilterFrom(''); setFilterTo('');
    fetchEntries({ member: '', docType: '', from: '', to: '' });
  };

  // ── NET CALCULATIONS ──────────────────────────────────────
  // Outstanding obligations = MDN - PCN (what member still owes)
  const totalMDN   = entries.filter(e => e.document_type === 'MDN').reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
  const totalPCN   = entries.filter(e => e.document_type === 'PCN').reduce((s, e) => s + parseFloat(e.credit || 0), 0);
  const netDebits  = Math.max(0, totalMDN - totalPCN);

  // Unutilised credit = MCN - CUN (what association still holds for member)
  const totalMCN   = entries.filter(e => e.document_type === 'MCN').reduce((s, e) => s + parseFloat(e.credit || 0), 0);
  const totalCUN   = entries.filter(e => e.document_type === 'CUN').reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
  const netCredits = Math.max(0, totalMCN - totalCUN);

  const netBalance = netDebits - netCredits;

  const downloadCurrentStatement = () => {
    setDownloading(true);
    const selectedMember = members.find(m => String(m.id) === String(filterMember));
    const memberSummaryRow = summary.find(m => String(m.id) === String(filterMember));
    generateStatementPDF({
      memberName: selectedMember?.full_name || (filterMember ? '' : 'All Members'),
      memberNumber: selectedMember?.member_number || '',
      entries, filterFrom, filterTo, netDebits, netCredits, netBalance,
      status: memberSummaryRow?.financial_status || null
    });
    setTimeout(() => setDownloading(false), 1000);
  };

  const downloadMemberStatement = async (memberId) => {
    try {
      const res = await axios.get(API, { headers, params: { member_id: memberId } });
      const memberEntries = res.data;
      const m = summary.find(x => String(x.id) === String(memberId));
      const mMDN = memberEntries.filter(e => e.document_type === 'MDN').reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
      const mPCN = memberEntries.filter(e => e.document_type === 'PCN').reduce((s, e) => s + parseFloat(e.credit || 0), 0);
      const mMCN = memberEntries.filter(e => e.document_type === 'MCN').reduce((s, e) => s + parseFloat(e.credit || 0), 0);
      const mCUN = memberEntries.filter(e => e.document_type === 'CUN').reduce((s, e) => s + parseFloat(e.debit  || 0), 0);
      const mNetDebits  = Math.max(0, mMDN - mPCN);
      const mNetCredits = Math.max(0, mMCN - mCUN);
      generateStatementPDF({
        memberName: m?.full_name || '', memberNumber: m?.member_number || '',
        entries: memberEntries, filterFrom: '', filterTo: '',
        netDebits: mNetDebits, netCredits: mNetCredits,
        netBalance: mNetDebits - mNetCredits, status: m?.financial_status || null
      });
    } catch (err) { showMessage('Download failed', 'error'); }
  };

  const inp       = { padding: '8px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '14px', width: '100%', boxSizing: 'border-box' };
  const inlineInp = { padding: '4px 6px', border: '1px solid #f39c12', borderRadius: '4px', fontSize: '12px', background: '#fffde7', width: '100%' };
  const lbl       = { display: 'block', marginBottom: '4px', fontWeight: '600', fontSize: '12px', color: '#555' };
  const fld       = { flex: '1', minWidth: '180px' };
  const smBtn     = (bg) => ({ background: bg, color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '12px' });

  return (
    <div style={{ padding: '24px', maxWidth: '1300px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#2c3e50', margin: 0 }}>MIFL — Member Individual Financial Ledger</h2>
          <p style={{ color: '#888', fontSize: '13px', margin: '4px 0 0' }}>Central definitive record — prima facie evidence per Article 18</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button onClick={() => setView(view === 'ledger' ? 'summary' : 'ledger')}
            style={{ background: '#6c757d', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            {view === 'ledger' ? 'Member Summary' : 'Full Ledger'}
          </button>
          {!readOnly && <button onClick={() => { setShowForm(!showForm); setMsg(''); }}
            style={{ background: '#2c3e50', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '13px' }}>
            {showForm ? 'Cancel' : '+ Post Entry'}
          </button>}
        </div>
      </div>

      {msg && (
        <div style={{ padding: '10px 16px', background: msgType === 'error' ? '#fde8e8' : '#d5f5e3', borderRadius: '6px', marginBottom: '16px', fontSize: '13px', color: msgType === 'error' ? '#c0392b' : '#1e8449' }}>
          {msg}
        </div>
      )}

      {!readOnly && showForm && (
        <div style={{ background: '#f8f9fa', border: '1px solid #ddd', borderRadius: '8px', padding: '20px', marginBottom: '20px' }}>
          <h4 style={{ margin: '0 0 16px', color: '#2c3e50' }}>Post MIFL Entry</h4>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={fld}>
              <label style={lbl}>Member *</label>
              <select style={inp} value={form.member_id} onChange={e => handleMemberChange(e.target.value)}>
                <option value=''>-- Select Member --</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>)}
              </select>
            </div>
            <div style={{ ...fld, maxWidth: '180px' }}>
              <label style={lbl}>Entry Date *</label>
              <input type='date' style={inp} value={form.entry_date} onChange={e => setForm({ ...form, entry_date: e.target.value })} />
            </div>
            <div style={{ ...fld, minWidth: '220px' }}>
              <label style={lbl}>Document Type *</label>
              <select style={inp} value={form.document_type} onChange={e => handleTypeChange(e.target.value)}>
                <option value='MDN'>MDN — Member Debit Note</option>
                <option value='PCN'>PCN — Payment Confirmation Note</option>
                <option value='MCN'>MCN — Member Credit Note</option>
                <option value='CUN'>CUN — Credit Utilisation Note</option>
              </select>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ ...fld, minWidth: '200px' }}>
              <label style={lbl}>Document Reference No. *</label>
              <input style={{ ...inp, background: '#f0f8f0', fontFamily: 'monospace', fontWeight: '600' }}
                value={form.document_number} onChange={e => setForm({ ...form, document_number: e.target.value })} />
            </div>
            <div style={{ flex: '2', minWidth: '300px' }}>
              <label style={lbl}>Description *</label>
              <input style={inp} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder='e.g. ROSCA Contribution May 2026 — missed deadline' />
            </div>
          </div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
            <div style={{ ...fld, maxWidth: '200px' }}>
              <label style={lbl}>Debit (UGX) {(form.document_type === 'MDN' || form.document_type === 'CUN') && <span style={{ color: '#e74c3c' }}>*</span>}</label>
              <input type='number' style={inp} value={form.debit} onChange={e => setForm({ ...form, debit: e.target.value })}
                disabled={form.document_type === 'PCN' || form.document_type === 'MCN'} placeholder='0' />
            </div>
            <div style={{ ...fld, maxWidth: '200px' }}>
              <label style={lbl}>Credit (UGX) {(form.document_type === 'PCN' || form.document_type === 'MCN') && <span style={{ color: '#27ae60' }}>*</span>}</label>
              <input type='number' style={inp} value={form.credit} onChange={e => setForm({ ...form, credit: e.target.value })}
                disabled={form.document_type === 'MDN'} placeholder='0' />
            </div>
          </div>
          {/* source_type — only for MDN */}
          {form.document_type === 'MDN' && (
            <div style={{ ...fld, minWidth: '200px' }}>
              <label style={lbl}>Obligation Source *</label>
              <select style={inp} value={form.source_type || ''} onChange={e => setForm({ ...form, source_type: e.target.value })}>
                <option value='manual'>Manual / General</option>
                <option value='fine'>Fine / Penalty</option>
                <option value='savings'>Monthly Savings</option>
                <option value='subscription'>Annual Subscription</option>
                <option value='rosca'>ROSCA Contribution</option>
                <option value='loan'>Loan Instalment</option>
              </select>
              <span style={{ fontSize: '11px', color: '#e74c3c' }}>Used for budget projections tracking</span>
            </div>
          )}
          {/* Mode of payment — only for PCN and MCN (cash received) */}
          {(form.document_type === 'PCN' || form.document_type === 'MCN') && (
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '12px' }}>
              <div style={{ ...fld, maxWidth: '220px' }}>
                <label style={{ ...lbl, color: '#e74c3c' }}>Received Into Account *</label>
                <select style={inp} value={form.mode_of_payment} onChange={e => setForm({ ...form, mode_of_payment: e.target.value })} required>
                  <option value=''>-- Select Account --</option>
                  <option value='cash'>Cash</option>
                  <option value='centenary'>Centenary Bank</option>
                  <option value='dfcu'>DFCU Bank</option>
                </select>
                <span style={{ fontSize: '11px', color: '#e74c3c' }}>Treasury will be credited on post</span>
              </div>
              <div style={{ ...fld, maxWidth: '220px' }}>
                <label style={lbl}>Receipt Reference (optional)</label>
                <input style={inp} value={form.receipt_ref} onChange={e => setForm({ ...form, receipt_ref: e.target.value })} placeholder='e.g. Bank ref, receipt no.' />
              </div>
            </div>
          )}
          {/* ── APPLY-TO PICKER — mandatory for every CUN ── */}
          {form.document_type === 'CUN' && form.member_id && (
            <div style={{ width: '100%', background: '#fdf6e3', border: '1px solid #b8860b', borderRadius: '6px', padding: '14px 16px', marginBottom: '12px' }}>
              <div style={{ fontWeight: 'bold', color: '#7d5a00', fontSize: '13px', marginBottom: '8px' }}>
                Apply This Credit To *
              </div>
              {loadingCreditTargets && <div style={{ color: '#7f8c8d', fontSize: '12px' }}>Loading member's credit position...</div>}
              {!loadingCreditTargets && creditTargets && (
                <>
                  <div style={{ fontSize: '12px', marginBottom: '10px' }}>
                    Available Credit: <strong>UGX {creditTargets.available_credit.toLocaleString()}</strong>
                  </div>
                  <select style={inp} value={form.apply_to_type}
                    onChange={e => {
                      const val = e.target.value;
                      const updates = {
                        apply_to_type: val, apply_fine_id: '',
                        apply_rosca_contribution_id: '', apply_savings_cycle_id: '', apply_savings_month: '', apply_subscription_cycle_id: '', apply_loan_id: '',
                        apply_rosca_upcoming_contribution_id: '', apply_savings_upcoming_cycle_id: '', apply_savings_upcoming_month: '',
                      };
                      if (val === 'rosca' && creditTargets.rosca) updates.apply_rosca_contribution_id = creditTargets.rosca.contribution_id;
                      if (val === 'savings' && creditTargets.savings) { updates.apply_savings_cycle_id = creditTargets.savings.cycle_id; updates.apply_savings_month = creditTargets.savings.month; }
                      if (val === 'subscription' && creditTargets.subscription) updates.apply_subscription_cycle_id = creditTargets.subscription.cycle_id;
                      if (val === 'rosca_upcoming' && creditTargets.rosca_upcoming) updates.apply_rosca_upcoming_contribution_id = creditTargets.rosca_upcoming.ref.contribution_id;
                      if (val === 'savings_upcoming' && creditTargets.savings_upcoming) { updates.apply_savings_upcoming_cycle_id = creditTargets.savings_upcoming.ref.cycle_id; updates.apply_savings_upcoming_month = creditTargets.savings_upcoming.ref.month; }
                      if (val === 'loan' && creditTargets.loans?.length === 1) updates.apply_loan_id = creditTargets.loans[0].loan_id;
                      setForm(prev => ({ ...prev, ...updates }));
                    }}>
                    <option value=''>-- Select target --</option>
                    <option value='fine' disabled={!creditTargets.fines.length}>Fine {creditTargets.fines.length ? `(${creditTargets.fines.length} outstanding)` : '(none outstanding)'}</option>
                    <option value='rosca' disabled={!creditTargets.rosca}>ROSCA Contribution {creditTargets.rosca ? `— UGX ${creditTargets.rosca.balance.toLocaleString()} due` : '(none due)'}</option>
                    <option value='savings' disabled={!creditTargets.savings}>Monthly Savings {creditTargets.savings ? `— UGX ${creditTargets.savings.balance.toLocaleString()} due` : '(none due)'}</option>
                    <option value='subscription' disabled={!creditTargets.subscription}>Annual Subscription {creditTargets.subscription ? `— UGX ${creditTargets.subscription.balance.toLocaleString()} due` : '(fully paid)'}</option>
                    {creditTargets.rosca_upcoming && (
                      <option value='rosca_upcoming'>ROSCA Contribution — pay early ({creditTargets.rosca_upcoming.label.split('— ')[1]}) — UGX {creditTargets.rosca_upcoming.due.toLocaleString()}</option>
                    )}
                    {creditTargets.savings_upcoming && (
                      <option value='savings_upcoming'>Monthly Savings — pay early ({creditTargets.savings_upcoming.label.split('— ')[1]}) — UGX {creditTargets.savings_upcoming.due.toLocaleString()}</option>
                    )}
                    {creditTargets.loans && creditTargets.loans.length > 0 && (
                      <option value='loan'>
                        Loan Repayment {creditTargets.loans.length === 1
                          ? `— ${creditTargets.loans[0].loan_number} — UGX ${parseFloat(creditTargets.loans[0].due).toLocaleString()} due`
                          : `(${creditTargets.loans.length} loans due)`}
                      </option>
                    )}
                    <option value='withdrawal'>Cash Withdrawal — pay out to member</option>
                  </select>

                  {form.apply_to_type === 'fine' && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={lbl}>Which Fine? *</label>
                      <select style={inp} value={form.apply_fine_id} onChange={e => setForm({ ...form, apply_fine_id: e.target.value })}>
                        <option value=''>-- Select fine --</option>
                        {creditTargets.fines.map(f => (
                          <option key={f.id} value={f.id}>{(f.fine_type || '').replace(/_/g, ' ')} — {f.reason} — UGX {parseFloat(f.balance).toLocaleString()}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {form.apply_to_type === 'rosca' && creditTargets.rosca && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      Applies to Month {creditTargets.rosca.month} — outstanding UGX {creditTargets.rosca.balance.toLocaleString()}
                    </div>
                  )}
                  {form.apply_to_type === 'savings' && creditTargets.savings && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      Applies to Month {creditTargets.savings.month} — outstanding UGX {creditTargets.savings.balance.toLocaleString()}
                    </div>
                  )}
                  {form.apply_to_type === 'subscription' && creditTargets.subscription && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      Cycle: {creditTargets.cycle_name} — outstanding UGX {creditTargets.subscription.balance.toLocaleString()}
                    </div>
                  )}
                  {form.apply_to_type === 'loan' && creditTargets.loans && creditTargets.loans.length > 1 && (
                    <div style={{ marginTop: '10px' }}>
                      <label style={lbl}>Which Loan? *</label>
                      <select style={inp} value={form.apply_loan_id} onChange={e => setForm({ ...form, apply_loan_id: e.target.value })}>
                        <option value=''>-- Select loan --</option>
                        {creditTargets.loans.map(l => (
                          <option key={l.loan_id} value={l.loan_id}>{l.loan_number} — UGX {parseFloat(l.due).toLocaleString()} due</option>
                        ))}
                      </select>
                    </div>
                  )}
                  {form.apply_to_type === 'loan' && creditTargets.loans && creditTargets.loans.length === 1 && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      {creditTargets.loans[0].loan_number} — UGX {parseFloat(creditTargets.loans[0].due).toLocaleString()} due this month
                    </div>
                  )}
                  {form.apply_to_type === 'rosca_upcoming' && creditTargets.rosca_upcoming && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      {creditTargets.rosca_upcoming.label} — not yet due, paying ahead — UGX {creditTargets.rosca_upcoming.due.toLocaleString()}
                    </div>
                  )}
                  {form.apply_to_type === 'savings_upcoming' && creditTargets.savings_upcoming && (
                    <div style={{ marginTop: '10px', fontSize: '12px', color: '#555' }}>
                      {creditTargets.savings_upcoming.label} — not yet due, paying ahead — UGX {creditTargets.savings_upcoming.due.toLocaleString()}
                    </div>
                  )}
                  {form.apply_to_type === 'withdrawal' && (
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginTop: '10px' }}>
                      <div style={{ ...fld, maxWidth: '220px' }}>
                        <label style={{ ...lbl, color: '#e74c3c' }}>Pay Out From Account *</label>
                        <select style={inp} value={form.mode_of_payment} onChange={e => setForm({ ...form, mode_of_payment: e.target.value })}>
                          <option value=''>-- Select Account --</option>
                          <option value='cash'>Cash</option>
                          <option value='centenary'>Centenary Bank</option>
                          <option value='dfcu'>DFCU Bank</option>
                        </select>
                        <span style={{ fontSize: '11px', color: '#e74c3c' }}>Treasury will be debited on post</span>
                      </div>
                      <div style={{ ...fld, maxWidth: '220px' }}>
                        <label style={lbl}>Receipt Reference (optional)</label>
                        <input style={inp} value={form.receipt_ref} onChange={e => setForm({ ...form, receipt_ref: e.target.value })} placeholder='e.g. Payment voucher no.' />
                      </div>
                    </div>
                  )}
                  {creditTargets.available_credit <= 0 && (
                    <div style={{ color: '#c0392b', fontSize: '12px', marginTop: '8px' }}>This member has no unutilised credit.</div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── MDN ALLOCATION CHECKLIST — only for PCN ── */}
          {form.document_type === 'PCN' && form.member_id && (
            <div style={{ width: '100%', background: '#f0f8ff', border: '1px solid #2980b9', borderRadius: '6px', padding: '14px 16px', marginBottom: '8px' }}>
              <div style={{ fontWeight: 'bold', color: '#1a5276', fontSize: '13px', marginBottom: '8px' }}>
                Allocate PCN Against Outstanding Debit Notes
              </div>
              {loadingMdns && <div style={{ color: '#7f8c8d', fontSize: '12px' }}>Loading outstanding MDNs...</div>}
              {!loadingMdns && outstandingMdns.length === 0 && (
                <div style={{ color: '#7f8c8d', fontSize: '12px' }}>No outstanding MDNs for this member — payment will be posted as general credit</div>
              )}
              {!loadingMdns && outstandingMdns.length > 0 && (
                <>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', marginBottom: '10px' }}>
                    <thead>
                      <tr style={{ background: '#1a5276', color: 'white' }}>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>✓</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>MDN Ref</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Description</th>
                        <th style={{ padding: '6px 8px', textAlign: 'left' }}>Source</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Original</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Outstanding</th>
                        <th style={{ padding: '6px 8px', textAlign: 'center' }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {outstandingMdns.map((mdn, i) => {
                        const checked = !!selectedMdns[mdn.id];
                        return (
                          <tr key={mdn.id} style={{ background: checked ? '#e8f4f8' : i % 2 === 0 ? '#f9f9f9' : 'white', cursor: 'pointer' }}
                            onClick={() => toggleMdn(mdn)}>
                            <td style={{ padding: '6px 8px' }}>
                              <input type="checkbox" checked={checked} onChange={() => {}} style={{ cursor: 'pointer' }} />
                            </td>
                            <td style={{ padding: '6px 8px', fontFamily: 'monospace', fontWeight: 'bold' }}>{mdn.document_number}</td>
                            <td style={{ padding: '6px 8px', maxWidth: '200px' }}>{mdn.description}</td>
                            <td style={{ padding: '6px 8px' }}>
                              <span style={{ background: mdn.source_type === 'fine' ? '#e74c3c' : mdn.source_type === 'savings' ? '#27ae60' : mdn.source_type === 'subscription' ? '#2980b9' : '#7f8c8d', color: 'white', padding: '1px 6px', borderRadius: '4px', fontSize: '10px' }}>
                                {mdn.source_type || 'manual'}
                              </span>
                            </td>
                            <td style={{ padding: '6px 8px', textAlign: 'right' }}>{Number(mdn.debit).toLocaleString()}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'right', color: '#e74c3c', fontWeight: 'bold' }}>{Number(mdn.amount_outstanding).toLocaleString()}</td>
                            <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                              <span style={{ background: mdn.status === 'partial' ? '#f39c12' : '#e74c3c', color: 'white', padding: '1px 6px', borderRadius: '4px', fontSize: '10px' }}>
                                {mdn.status || 'outstanding'}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {/* Allocation counter */}
                  <div style={{ display: 'flex', gap: '20px', padding: '10px 12px', background: allocationRemaining < 0 ? '#fde8e8' : allocationRemaining === 0 ? '#d5f5e3' : '#fffde7', borderRadius: '4px', fontSize: '12px' }}>
                    <span>PCN Amount: <strong>UGX {pcnAmount.toLocaleString()}</strong></span>
                    <span>Allocated: <strong style={{ color: '#1a5276' }}>UGX {totalAllocated.toLocaleString()}</strong></span>
                    <span>Remaining: <strong style={{ color: allocationRemaining < 0 ? '#c0392b' : allocationRemaining > 0 ? '#e67e22' : '#27ae60' }}>
                      UGX {allocationRemaining.toLocaleString()}
                    </strong></span>
                    {allocationRemaining > 0 && <span style={{ color: '#e67e22' }}>⚠ Excess will auto-generate MCN</span>}
                    {allocationRemaining < 0 && <span style={{ color: '#c0392b' }}>⚠ Allocation exceeds PCN amount</span>}
                    {allocationRemaining === 0 && totalAllocated > 0 && <span style={{ color: '#27ae60' }}>✓ Fully allocated</span>}
                  </div>
                </>
              )}
            </div>
          )}

          <button onClick={handleSubmit}
            style={{ background: DOC_COLORS[form.document_type], color: 'white', border: 'none', padding: '10px 24px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' }}>
            Post {DOC_LABELS[form.document_type]}
          </button>
        </div>
      )}

      {/* ── SUMMARY VIEW ── */}
      {view === 'summary' && (
        <div>
          <h3 style={{ color: '#2c3e50', marginBottom: '12px' }}>Member Financial Status</h3>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {['good_standing', 'in_arrears', 'in_credit'].map(s => (
              <div key={s} style={{ padding: '12px 20px', background: STATUS_COLORS[s], color: 'white', borderRadius: '8px', minWidth: '140px' }}>
                <div style={{ fontSize: '11px', opacity: 0.85 }}>{STATUS_LABELS[s]}</div>
                <div style={{ fontSize: '24px', fontWeight: '700' }}>{summary.filter(m => m.financial_status === s).length}</div>
              </div>
            ))}
            <div style={{ padding: '12px 20px', background: '#2c3e50', color: 'white', borderRadius: '8px', minWidth: '140px' }}>
              <div style={{ fontSize: '11px', opacity: 0.85 }}>Total Active</div>
              <div style={{ fontSize: '24px', fontWeight: '700' }}>{summary.length}</div>
            </div>
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#2c3e50', color: 'white' }}>
                {['Member', 'No.', 'Outstanding Obligations (Net)', 'Unutilised Credit (Net)', 'Current Balance', 'Status', 'Entries', 'Action'].map(h => (
                  <th key={h} style={{ padding: '10px', textAlign: 'left' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {summary.map((m, i) => (
                <tr key={m.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: '1px solid #eee' }}>
                  <td style={{ padding: '10px', fontWeight: '600' }}>{m.full_name}</td>
                  <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>{m.member_number}</td>
                  <td style={{ padding: '10px', color: '#e74c3c', fontWeight: '600' }}>
                    {parseFloat(m.net_debits) > 0 ? 'UGX ' + Number(m.net_debits).toLocaleString() : <span style={{ color: '#2ecc71' }}>✓ Cleared</span>}
                  </td>
                  <td style={{ padding: '10px', color: '#2980b9', fontWeight: '600' }}>
                    {parseFloat(m.net_credits) > 0 ? 'UGX ' + Number(m.net_credits).toLocaleString() : '—'}
                  </td>
                  <td style={{ padding: '10px', fontWeight: '700', color: parseFloat(m.current_balance) > 0 ? '#e74c3c' : parseFloat(m.current_balance) < 0 ? '#2980b9' : '#1e8449' }}>
                    UGX {Number(m.current_balance || 0).toLocaleString()}
                  </td>
                  <td style={{ padding: '10px' }}>
                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '600', background: STATUS_COLORS[m.financial_status] || '#95a5a6', color: 'white' }}>
                      {STATUS_LABELS[m.financial_status] || 'Good Standing'}
                    </span>
                  </td>
                  <td style={{ padding: '10px', textAlign: 'center', color: '#666' }}>{m.entry_count || 0}</td>
                  <td style={{ padding: '10px' }}>
                    <div style={{ display: 'flex', gap: '4px' }}>
                      {parseInt(m.entry_count) > 0 && (
                        <button onClick={() => drillToMember(m.id)} style={{ background: '#eaf4fb', color: '#1a5276', border: '1px solid #aed6f1', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                          View Ledger
                        </button>
                      )}
                      <button onClick={() => downloadMemberStatement(m.id)}
                        style={{ background: '#d5f5e3', color: '#1e8449', border: '1px solid #a9dfbf', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>
                        Download
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── LEDGER VIEW ── */}
      {view === 'ledger' && (
        <div>
          <div style={{ background: '#f9f9f9', padding: '14px 16px', borderRadius: '8px', marginBottom: '14px', display: 'flex', gap: '12px', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: '200px' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Member</label>
              <select style={{ padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                value={filterMember} onChange={e => { const val = e.target.value; setFilterMember(val); fetchEntries({ member: val }); }}>
                <option value=''>All Members</option>
                {members.map(m => <option key={m.id} value={m.id}>{m.full_name} — {m.member_number}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>Document Type</label>
              <select style={{ padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }}
                value={filterDocType} onChange={e => { const val = e.target.value; setFilterDocType(val); fetchEntries({ docType: val }); }}>
                <option value=''>All Types</option>
                <option value='MDN'>MDN — Debit Note</option>
                <option value='PCN'>PCN — Payment Confirmation</option>
                <option value='MCN'>MCN — Credit Note</option>
                <option value='CUN'>CUN — Credit Utilisation</option>
              </select>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>From Date</label>
              <input type='date' style={{ padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} value={filterFrom} onChange={e => setFilterFrom(e.target.value)} />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              <label style={{ fontSize: '11px', fontWeight: 'bold', color: '#7f8c8d' }}>To Date</label>
              <input type='date' style={{ padding: '7px', border: '1px solid #ddd', borderRadius: '4px', fontSize: '13px' }} value={filterTo} onChange={e => setFilterTo(e.target.value)} />
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <button onClick={applyFilters} style={{ background: '#2c3e50', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Filter</button>
              <button onClick={clearFilters} style={{ background: '#95a5a6', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>Clear</button>
              <button onClick={downloadCurrentStatement} disabled={downloading}
                style={{ background: downloading ? '#95a5a6' : '#27ae60', color: 'white', border: 'none', padding: '7px 14px', borderRadius: '4px', cursor: downloading ? 'default' : 'pointer', fontSize: '13px', fontWeight: '600' }}>
                {downloading ? 'Generating...' : 'Download Statement'}
              </button>
            </div>
          </div>

          {/* ── KPI CARDS — NET FIGURES ── */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <div style={{ background: '#e74c3c', color: 'white', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
              Outstanding Obligations: UGX {Number(netDebits).toLocaleString()}
              <div style={{ fontSize: '10px', opacity: 0.8, fontWeight: 'normal', marginTop: '2px' }}>MDN {Number(totalMDN).toLocaleString()} − PCN {Number(totalPCN).toLocaleString()}</div>
            </div>
            <div style={{ background: '#27ae60', color: 'white', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
              Unutilised Credit: UGX {Number(netCredits).toLocaleString()}
              <div style={{ fontSize: '10px', opacity: 0.8, fontWeight: 'normal', marginTop: '2px' }}>MCN {Number(totalMCN).toLocaleString()} − CUN {Number(totalCUN).toLocaleString()}</div>
            </div>
            <div style={{ background: netBalance > 0 ? '#e74c3c' : netBalance < 0 ? '#2980b9' : '#2c3e50', color: 'white', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>
              Net: UGX {Number(Math.abs(netBalance)).toLocaleString()} {netBalance > 0 ? '(Owed to Association)' : netBalance < 0 ? '(Credit Held)' : '(Balanced)'}
            </div>
            <div style={{ background: '#ecf0f1', padding: '7px 14px', borderRadius: '6px', fontSize: '12px', color: '#666' }}>
              {entries.length} records
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: '#2c3e50', color: 'white' }}>
                  {['Date', 'Member', 'Type', 'Reference', 'Description', 'Debit (UGX)', 'Credit (UGX)', 'Balance (UGX)', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px', textAlign: h.includes('UGX') ? 'right' : 'left', fontSize: '12px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan='9' style={{ padding: '40px', textAlign: 'center', color: '#aaa' }}>No MIFL entries</td></tr>
                )}
                {entries.map((e, i) => (
                  editingId === e.id ? (
                    <tr key={e.id} style={{ background: '#fffde7', borderLeft: '4px solid #f39c12' }}>
                      <td style={{ padding: '8px' }}><input type="date" value={editData.entry_date} onChange={x => setEditData({ ...editData, entry_date: x.target.value })} style={inlineInp} /></td>
                      <td style={{ padding: '8px' }}><strong>{e.member_name}</strong></td>
                      <td style={{ padding: '8px' }}>
                        <select value={editData.document_type} onChange={x => setEditData({ ...editData, document_type: x.target.value })} style={inlineInp}>
                          {['MDN', 'PCN', 'MCN', 'CUN'].map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </td>
                      <td style={{ padding: '8px' }}><input value={editData.document_number} onChange={x => setEditData({ ...editData, document_number: x.target.value })} style={inlineInp} /></td>
                      <td style={{ padding: '8px' }}><input value={editData.description} onChange={x => setEditData({ ...editData, description: x.target.value })} style={{ ...inlineInp, minWidth: '200px' }} /></td>
                      <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={editData.debit} onChange={x => setEditData({ ...editData, debit: x.target.value })} style={{ ...inlineInp, textAlign: 'right' }} /></td>
                      <td style={{ padding: '8px', textAlign: 'right' }}><input type="number" value={editData.credit} onChange={x => setEditData({ ...editData, credit: x.target.value })} style={{ ...inlineInp, textAlign: 'right' }} /></td>
                      <td style={{ padding: '8px', textAlign: 'right', color: '#f39c12', fontStyle: 'italic', fontSize: '11px' }}>Recalculated on save</td>
                      <td style={{ padding: '8px' }}>
                        {!readOnly && <button onClick={() => saveEdit(e.id)} style={{ ...smBtn('#2ecc71'), marginRight: '4px' }}>Save</button>}
                        <button onClick={() => setEditingId(null)} style={smBtn('#95a5a6')}>Cancel</button>
                      </td>
                    </tr>
                  ) : (
                    <tr key={e.id} style={{ background: i % 2 === 0 ? '#f9f9f9' : 'white', borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '10px' }}>{e.entry_date?.slice(0, 10)}</td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ fontWeight: '600' }}>{e.member_name}</div>
                        <div style={{ color: '#888', fontSize: '11px' }}>{e.member_number}</div>
                      </td>
                      <td style={{ padding: '10px' }}>
                        <span style={{ background: DOC_COLORS[e.document_type] || '#95a5a6', color: 'white', padding: '3px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700' }}>{e.document_type}</span>
                        <div style={{ color: '#888', fontSize: '11px', marginTop: '2px' }}>{DOC_LABELS[e.document_type]}</div>
                      </td>
                      <td style={{ padding: '10px', fontFamily: 'monospace', fontSize: '12px' }}>{e.document_number}</td>
                      <td style={{ padding: '10px' }}>{e.description}</td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#e74c3c', fontWeight: parseFloat(e.debit) > 0 ? '700' : 'normal' }}>
                        {parseFloat(e.debit) > 0 ? Number(e.debit).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', color: '#1e8449', fontWeight: parseFloat(e.credit) > 0 ? '700' : 'normal' }}>
                        {parseFloat(e.credit) > 0 ? Number(e.credit).toLocaleString() : '—'}
                      </td>
                      <td style={{ padding: '10px', textAlign: 'right', fontWeight: '700', color: parseFloat(e.balance) > 0 ? '#e74c3c' : parseFloat(e.balance) < 0 ? '#2980b9' : '#1e8449' }}>
                        {Number(e.balance).toLocaleString()}
                      </td>
                      <td style={{ padding: '10px' }}>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          <button onClick={() => generateEntryDocument(e, headers)}                            style={{ background: DOC_COLORS[e.document_type] || '#2c3e50', color: 'white', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px', fontWeight: 'bold' }}>
                            📄 {e.document_type}
                          </button>
                          {!readOnly && <button onClick={() => { setEditingId(e.id); setEditData({ entry_date: e.entry_date?.slice(0, 10) || '', document_type: e.document_type, document_number: e.document_number, description: e.description, debit: e.debit, credit: e.credit }); }} style={smBtn('#e67e22')}>Edit</button>}
                          {!readOnly && <button onClick={() => handleDelete(e.id)} style={{ background: '#fde8e8', color: '#c0392b', border: '1px solid #f5c6cb', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}>Del</button>}
                        </div>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          </div>

          <div style={{ marginTop: '16px', padding: '10px 14px', background: '#eaf4fb', borderLeft: '4px solid #2980b9', borderRadius: '4px', fontSize: '12px', color: '#555' }}>
            <strong>Article 18:</strong> MIFL records only defaults and overpayments. Regular on-time payments do not appear here. The MIFL balance is prima facie evidence in any legal proceeding.
          </div>
        </div>
      )}
    </div>
  );
}

export default Mifl;